import React, { useState, useEffect, useCallback } from 'react';
import { Select, Modal, Form, Input, Button, Tag, Space, message } from 'antd';
import { UserAddOutlined, SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { studentAPI, classAPI } from '../services/api';

const { Option } = Select;

interface Student {
  id: number;
  name: string;
  class_id: number;
  class_name?: string;  // 兼容旧的 API 响应
  class?: {             // 后端返回的完整 Class 对象
    id: number;
    name: string;
    grade?: number;
  };
}

interface Class {
  id: number;
  name: string;
  grade: number;
  student_count: number;
}

interface StudentSelectorProps {
  value?: { studentId?: number; classId?: number };
  onChange?: (value: { studentId: number; classId: number; studentName: string; className?: string }) => void;
  placeholder?: string;
  disabled?: boolean;
  excludeIds?: number[];  // 需要排除的学生ID列表
  classes?: Class[];  // 从父组件接收班级列表
  onLoadStudents?: (classId: number) => Promise<Student[]>;  // 加载学生的回调函数
  className?: string;
}

const StudentSelector: React.FC<StudentSelectorProps> = ({
  value = {},
  onChange,
  placeholder = '输入学生姓名搜索或添加新学生',
  disabled = false,
  excludeIds = [],
  classes: propsClasses,
  onLoadStudents,
  className
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchMode, setSearchMode] = useState<'class' | 'search'>('search'); // 默认为搜索模式
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | undefined>(value.classId);
  const [selectedStudent, setSelectedStudent] = useState<Student | undefined>();
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form] = Form.useForm();
  const [localClassesLoaded, setLocalClassesLoaded] = useState(false);
  const [initialStudentsLoaded, setInitialStudentsLoaded] = useState(false);

  // 加载班级列表
  useEffect(() => {
    // 如果父组件提供了班级列表，直接使用
    if (propsClasses && propsClasses.length > 0) {
      setClasses(propsClasses);
    } else if (!localClassesLoaded) {
      // 否则自己加载（但只加载一次）
      loadClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsClasses]);

  // 如果有初始值，加载学生信息
  useEffect(() => {
    if (value.studentId) {
      loadStudentInfo(value.studentId);
    }
  }, [value.studentId]);

  // 当value.classId变化时，更新selectedClass和加载学生
  useEffect(() => {
    if (value.classId !== undefined) {
      setSelectedClass(value.classId);
      // 如果有班级ID且没有学生ID（即新添加的行），加载该班级的学生
      if (value.classId && value.classId > 0 && !value.studentId) {
        // 组件初始化时必须加载学生，或者班级变化时重新加载
        if (!initialStudentsLoaded || selectedClass !== value.classId) {
          setInitialStudentsLoaded(true);
          handleClassChange(value.classId, false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.classId]);  // 移除value.studentId依赖，避免不必要的触发

  const loadClasses = async () => {
    // 如果已经加载过或有父组件提供的数据，不重复加载
    if (localClassesLoaded || (propsClasses && propsClasses.length > 0)) return;
    
    setLocalClassesLoaded(true);
    try {
      const response = await classAPI.list({ page_size: 100 });
      setClasses(response.data.data || []);
    } catch (error) {
      message.error('加载班级列表失败');
    }
  };

  const loadStudentInfo = async (studentId: number) => {
    try {
      const response = await studentAPI.get(studentId);
      const student = response.data.student;
      setSelectedStudent(student);
      setSelectedClass(student.class_id);
      setSearchValue(student.name);
    } catch (error) {
      console.error('加载学生信息失败', error);
    }
  };

  // 搜索学生（防抖处理）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce(async (keyword: string) => {
      if (!keyword || keyword.length < 1) {
        // 如果没有搜索关键词且有选中班级，显示该班级的学生
        if (selectedClass && searchMode === 'class') {
          handleClassChange(selectedClass, false);
        } else {
          setStudents([]);
        }
        return;
      }

      setLoading(true);
      try {
        const response = await studentAPI.autocomplete(keyword);
        setStudents(response.data.suggestions || []);
      } catch (error) {
        message.error('搜索失败');
      } finally {
        setLoading(false);
      }
    }, 300),
    [selectedClass, searchMode]
  );

  // 选择学生
  const handleSelect = (studentId: number) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setSelectedStudent(student);
      setSelectedClass(student.class_id);
      
      if (onChange) {
        onChange({
          studentId: student.id,
          classId: student.class_id,
          studentName: student.name,
          className: student.class?.name || student.class_name || ''
        });
      }
    }
  };

  // 显示添加学生弹窗
  const handleShowAddModal = () => {
    // 如果没有选择班级，提示用户选择
    if (!selectedClass && classes.length > 0) {
      message.warning('请先选择班级后再添加学生');
      return;
    }
    
    form.setFieldsValue({
      name: searchValue,
      class_id: selectedClass
    });
    setShowAddModal(true);
  };

  // 快速创建学生
  const handleQuickCreate = async (values: any) => {
    try {
      const response = await studentAPI.quickCreate({
        name: values.name,
        class_id: values.class_id
      });

      const { student, created } = response.data;
      
      if (created) {
        message.success('学生创建成功');
      } else {
        message.info('该学生已存在');
      }

      // 确保student对象有完整的班级信息
      const completeStudent = {
        ...student,
        class_name: student.class?.name || classes.find(c => c.id === student.class_id)?.name || '',
        class: student.class || classes.find(c => c.id === student.class_id)
      };

      // 将新创建的学生添加到列表中
      setStudents(prevStudents => {
        // 检查是否已存在（避免重复）
        const exists = prevStudents.some(s => s.id === completeStudent.id);
        if (!exists) {
          return [...prevStudents, completeStudent];
        }
        return prevStudents;
      });

      setSelectedStudent(completeStudent);
      setSelectedClass(completeStudent.class_id);
      setSearchValue(completeStudent.name);
      setShowAddModal(false);
      form.resetFields();

      if (onChange) {
        onChange({
          studentId: completeStudent.id,
          classId: completeStudent.class_id,
          studentName: completeStudent.name,
          className: completeStudent.class_name || completeStudent.class?.name || ''
        });
      }
    } catch (error) {
      message.error('创建学生失败');
    }
  };

  // 班级变更
  const handleClassChange = async (classId: number, clearSelection: boolean = true) => {
    setSelectedClass(classId);
    setSearchMode('class'); // 切换到班级模式
    // 只在需要时清空已选学生
    if (clearSelection) {
      setSelectedStudent(undefined);
      setSearchValue('');
    }
    
    // 加载该班级的所有学生
    setLoading(true);
    try {
      // 优先使用父组件提供的加载函数（带缓存）
      if (onLoadStudents) {
        const students = await onLoadStudents(classId);
        setStudents(students);
      } else {
        // 否则自己加载
        const response = await studentAPI.list({ 
          class_id: classId,
          page_size: 100 
        });
        setStudents(response.data.data || []);
      }
    } catch (error) {
      message.error('加载学生列表失败');
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <div className={`student-selector ${className || ''}`}>
        <Space.Compact style={{ width: '100%' }}>
          <Select
            style={{ width: '35%' }}
            placeholder="选择班级（可选）"
            value={selectedClass}
            onChange={(classId) => handleClassChange(classId, true)}
            disabled={disabled}
            showSearch
            allowClear
            onClear={() => {
              setSelectedClass(undefined);
              setSearchMode('search');
              setStudents([]);
            }}
            optionFilterProp="children"
          >
            {classes.map(cls => (
              <Option key={cls.id} value={cls.id}>
                {cls.name} ({cls.student_count}人)
              </Option>
            ))}
          </Select>

          <Select
            style={{ width: '65%' }}
            value={selectedStudent?.id}
            onChange={(value) => {
              if (value === -1) {
                handleShowAddModal();
              } else {
                handleSelect(value);
              }
            }}
            placeholder={selectedClass ? '选择或搜索学生' : '输入学生姓名搜索'}
            disabled={disabled}
            showSearch
            filterOption={false} // 禁用本地过滤，使用服务端搜索
            onSearch={(value) => {
              setSearchValue(value);
              if (value) {
                setSearchMode('search');
                handleSearch(value);
              } else if (selectedClass) {
                // 如果清空搜索且有选中班级，回到班级模式
                setSearchMode('class');
                handleClassChange(selectedClass, false);
              } else {
                setStudents([]);
              }
            }}
            onDropdownVisibleChange={(open) => {
              // 下拉框打开时的处理
              if (open) {
                if (searchValue) {
                  // 如果有搜索关键词，执行搜索
                  handleSearch(searchValue);
                } else if (selectedClass && searchMode === 'class') {
                  // 如果选中了班级且在班级模式，加载班级学生
                  handleClassChange(selectedClass, false);
                }
              }
            }}
            notFoundContent={loading ? '加载中...' : 
              (searchValue && students.length === 0 ? '未找到匹配的学生' : 
               (!searchValue && !selectedClass ? '请选择班级或输入学生姓名搜索' : '暂无学生数据'))}
            suffixIcon={<SearchOutlined />}
          >
            {students
              .filter(student => !excludeIds.includes(student.id))  // 过滤掉已选择的学生
              .map(student => (
                <Select.Option key={student.id} value={student.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{student.name}</span>
                    <Space size="small">
                      <Tag color="blue">{student.class?.name || student.class_name || ''}</Tag>
                    </Space>
                  </div>
                </Select.Option>
              ))}
            {searchValue && students.length === 0 && !loading && (
              <Select.Option value={-1}>
                <div style={{ color: '#1890ff' }}>
                  <UserAddOutlined /> 添加新学生 "{searchValue}"
                </div>
              </Select.Option>
            )}
          </Select>
        </Space.Compact>

        {selectedStudent && typeof selectedStudent === 'object' && (
          <div style={{ marginTop: 8 }}>
            <Tag color="green">
              已选择: {selectedStudent.name || '未知'} | {selectedStudent.class_name || selectedStudent.class?.name || '未知班级'}
            </Tag>
          </div>
        )}
      </div>

      <Modal
        title="添加新学生"
        open={showAddModal}
        onCancel={() => {
          setShowAddModal(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleQuickCreate}
        >
          <Form.Item
            name="name"
            label="学生姓名"
            rules={[{ required: true, message: '请输入学生姓名' }]}
          >
            <Input placeholder="请输入学生姓名" />
          </Form.Item>

          <Form.Item
            name="class_id"
            label="所属班级"
            rules={[{ required: true, message: '请选择班级' }]}
          >
            <Select placeholder="请选择班级" disabled>
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认添加
              </Button>
              <Button onClick={() => {
                setShowAddModal(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default StudentSelector;
